import { type ActivityItemType, RouteModel } from '@polaris/core/models'
import { groupRoutesByTimeSpan } from '@polaris/core/utils'
import { useInfiniteQuery } from '@tanstack/react-query'
import type { GetHistoryRequest } from '@/src/services/history'
import { getHistoryQuery } from '../keys/history'

interface GetHistorySelectors {
  type?: ActivityItemType
  hideExpired?: boolean
}

export const useTimeSpanGroupedHistory = (
  props: GetHistoryRequest & GetHistorySelectors & { enabled?: boolean },
) => {
  return useInfiniteQuery({
    ...getHistoryQuery(props),
    enabled: props.enabled,
    select: (data) => {
      const routes = data.pages
        .flatMap((page) => page.routes)
        .filter((route) =>
          typeof route === 'string'
            ? true
            : props.hideExpired
              ? new RouteModel(route).status.type !== 'expired'
              : true,
        )
      const rows = groupRoutesByTimeSpan(routes)

      const result = rows.map((route) =>
        typeof route === 'string' ? route : new RouteModel(route),
      )

      if (props.type) {
        return (
          result
            .map((entry) =>
              typeof entry === 'string'
                ? entry
                : entry.status.type === props.type
                  ? entry
                  : undefined,
            )
            /**
             * Because we are mapping out (undefining) the history entries
             * that are not of the type slected, we need to then
             * filter out the undefined array entries.
             *
             * Because of some TS Linting bugs, it's actually needed to
             * type cast to the correct interface.
             */
            .filter(Boolean) as (string | RouteModel)[]
        )
      }

      return result
    },
    // 30 seconds - we manually invalidate history during route execution
    // and explicitly poll history if any route has a "running" step status.
    staleTime: 30_000,
  })
}
